import { Injectable, OnDestroy } from '@angular/core';
import { NgbModal }              from '@ng-bootstrap/ng-bootstrap';
import { Subscription }          from 'rxjs';
import { TranslateService }      from '@ngx-translate/core';

import { AddDealershipComponent }             from './company.add-dealership.component';
import { AnnualSubscriptionComponent }        from './company.annual-subscription.component';
import { ApiService }                         from '../api/api.service';
import { BuySmsComponent }                    from './company.buy-sms.component';
import { CancelSubscriptionComponent }        from './company.cancel-subscription.component';
import { CodeTypeModel }                      from '../core/models/code-type';
import { CompanyCountryValidationsModel }     from './models/company.country-validations';
import { CompanyLabelsModel }                 from './models/company.labels';
import { CompanyMessagesModel }               from './models/company.messages';
import { CompanySettingsModel }               from './models/company.settings';
import { CompanySetUpComponent }              from './company.set-up.component';
import { CompanySubscriptionPlansModel }      from './models/company.subscription-plans';
import { ConfirmService }                     from '../messages/confirm/confirm.service';
import { COUNTRIES }                          from '../shared/constants/countries.constant';
import { MyobService }                        from '../myob/myob.service';
import { QuickbooksService }                  from '../quickbooks/quickbooks.service';
import { SessionModel }                       from '../sessions/models/session';
import { SessionService }                     from '../sessions/session.service';
import { ShareAccoutingComponent }            from './company.share-accounting.component';
import { SmsCostModel }                       from './models/sms-cost';
import { UpdateSubscriptionPaymentComponent } from './company.update-subscription-payment.component';
import { UtcOffset }                          from './models/utc-offset';
import { XeroService }                        from '../xero/xero.service';

@Injectable({
  providedIn: 'root'
})
export class CompanyService implements OnDestroy {
  private _COUNTRIES = COUNTRIES;
  private _sessionRefreshSubscription : Subscription;

  company : any = this.sessionService.company();
  labels  : CompanyLabelsModel = {};

  private letterheadVersion = Date.now();
  private logoVersion       = Date.now();

  constructor (
    private apiService       : ApiService,
    private translateService : TranslateService,
    private modalService     : NgbModal,
    private confirmService   : ConfirmService,
    private myobService      : MyobService,
    private quickbooksService: QuickbooksService,
    private sessionService   : SessionService,
    private xeroService      : XeroService
  ) {
    this.setCompany();

    if (this.company) {
      this.labels = this.getLabels();
    }

    this._sessionRefreshSubscription = this.sessionService.sessionRefresh$.subscribe(( session : SessionModel ) => {
      this.setCompany();

      this.labels = this.getLabels();
    });
  }

  ngOnDestroy () : void {
    if (this._sessionRefreshSubscription) {
      this._sessionRefreshSubscription.unsubscribe();
    }
  }

  addDealership () : Promise<any> {
    return this.apiService.get('/company/quote_for_workshop_dealership_conversion')
    .then(( quote : number ) => {
      const modal = this.modalService.open(AddDealershipComponent, {
        windowClass: 'lg-modal'
      });

      modal.componentInstance.quote = quote;

      return modal.result;
    });
  }

  annualSubscription () : Promise<boolean> {
    const modal = this.modalService.open(AnnualSubscriptionComponent, {
      windowClass: 'lg-modal'
    });

    return modal.result;
  }

  areServiceAdvisersEnabledOnReports () : boolean {
    return this.isSubscriptionPlan('gold', false)
      && this.company.adviser_report;
  }

  areTrackingCategoriesAvailable () : boolean {
    return this.hasIntegration('xero')
      && this.company.company_settings.xero_tracking_categories_status;
  }

  areTrackingCategoryOptionsAvailable () : boolean {
    return this.areTrackingCategoriesAvailable()
      && !!this.company.company_accounting_settings.xero_tracking_category_id;
  }

  buySms () : Promise<number> {
    return this.apiService.get('/sms_logs/current_sms_cost')
    .then(( cost : SmsCostModel ) => {
      const modal = this.modalService.open(BuySmsComponent);

      modal.componentInstance.cost = cost;
      modal.componentInstance.count = this.company.sms_count_left;

      return modal.result;
    });
  }

  cancelSubscription ( subscription : any ) : Promise<void> {
    const modal = this.modalService.open(CancelSubscriptionComponent, {
      windowClass: 'md-modal'
    });

    modal.componentInstance.subscription = subscription;

    return modal.result;
  }

  confirmAccountingIntegration () : ng.IPromise<void> {
    return this.confirmService.generic(this.translateService.instant('JS_SPACE.INTEGRATIONS.ACTIVATION_WARNING'), 'warning', {
      cancelText : 'Cancel',
      confirmText: 'Continue',
      size       : 'lg'
    });
  }

  confirmAccountSharing () : Promise<boolean> {
    return this.apiService.get('/company/share_accounting', true);
  }

  confirmAccountSharingPrefix ( store_prefix : string ) : Promise<any> {
    return this.apiService.patch('/company_accounting_settings', {
      company_accounting_setting: {
        company_id: this.company.id,
        store_prefix
      }
    });
  }

  country ( country : string | Array<string> ) : boolean {
    /**
     * In the customer app, there is no company to set to.
     * But, the session object returns the country code so
     * set it to that if the company doesn't exist.
     *
     * REFERENCE: WS-5684
     */
    const countryCode = this.company
      ? this.company.country_code
      : this.sessionService.base().country_code;

    if (country instanceof Array) {
      for (let i=0; i<country.length; i++) {
        if (countryCode === this._COUNTRIES[country[i]]) {
          return true;
        }
      }

      return false;
    }

    return countryCode === this._COUNTRIES[country];
  }

  getAccountingSettings () : Promise<any> {
    return this.apiService.get('/company_accounting_settings');
  }

  getAccountName ( code : string, accounts : Array<any> ) : string {
    let account : any = {};

    if (accounts && accounts.length) {
      account = accounts.find(option => option.code === code);

      if (account) {
        return account.description || '';
      }
    }

    return '';
  }

  getAllAccounts () : Promise<any> {
    if (this.hasIntegration('myob')) {
      return this.myobService.getOptions()
      .then(( response : any ) => {
        return {
          purchase: response.expense_accounts.map(( account : any ) => {
            return {
              code       : account.UID,
              description: account.Name
            };
          }),
          sale: response.income_accounts.map(( account : any ) => {
            return {
              code       : account.UID,
              description: account.Name
            };
          })
        };
      });
    }
    else if (this.hasIntegration('quickbooks')) {
      return this.quickbooksService.getAccounts()
      .then(( accounts : any ) => {
        return {
          purchase: accounts.expense_accounts.map(( account : any ) => {
            return {
              code       : account.id,
              description: account.name
            };
          }),
          sale: accounts.income_accounts.map(( account : any ) => {
            return {
              code       : account.id,
              description: account.name
            };
          })
        };
      });
    }
    else if (this.hasIntegration('xero')) {
      return this.xeroService.getSettings()
      .then(( response : any ) => {
        return {
          purchase: response.chart_of_accounts.expense_accounts,
          sale    : response.chart_of_accounts.revenue_accounts
        };
      });
    }

    return Promise.resolve([]);
  }

  getCategoryName ( id : string, categories : any ) : string {
    const category = categories.find(( option : any ) => option.id === id);

    return category
      ? category.description
      : '';
  }

  getCompanyTypes () : Promise<Array<CodeTypeModel>> {
    return this.apiService.get('/company/get_workshop_types');
  }

  getDefaultMessages () : Promise<CompanyMessagesModel> {
    return this.apiService.get('/company_message_settings');
  }

  getId () : string {
    return this.company.id;
  }

  getLabels () : CompanyLabelsModel {
    var labels : CompanyLabelsModel = {};

    // Employee title
    if (this.company.company_variable_fields && this.company.company_variable_fields.employee_title_name) {
      labels.employeeTitle = this.company.company_variable_fields.employee_title_name;
    }
    else {
      labels.employeeTitle = 'Mechanic';
    }

    // Business license title
    if (this.country('australia') && this.company.state === 'NSW') {
      labels.stateBusinessLicense = this.translateService.instant('JS_SPACE.LABELS.MVR_LICENSE_NO');
    }
    else if (this.country('australia') && this.company.state === 'WA') {
      labels.stateBusinessLicense = this.translateService.instant('JS_SPACE.LABELS.MRB_LICENSE_NO');
    }

    // Member Number
    if ( this.company.company_variable_fields
      && this.company.company_variable_fields.member_number_name
      && this.usesMemberNumbers()
    ) {
      labels.memberNumberName = this.company.company_variable_fields.member_number_name;
    }
    else {
      labels.memberNumberName = 'Member Number';
    }

    // Fleet Code
    if ( this.company.company_variable_fields && this.company.company_variable_fields.fleet_code_name ) {
      labels.fleetCode = this.company.company_variable_fields.fleet_code_name;
    }
    else {
      labels.fleetCode = this.translateService.instant('VEHICLE_SPACE.FLEET_CODE');
    }

    // Rego
    if ( this.company.company_variable_fields && this.company.company_variable_fields.plate_number_name ) {
      labels.rego = this.company.company_variable_fields.plate_number_name;
    }
    else {
      labels.rego = this.sessionService.countrySettings().rego_label;
    }

    // VIN
    if ( this.company.company_variable_fields && this.company.company_variable_fields.vin_name ) {
      labels.vin = this.company.company_variable_fields.vin_name;
    }
    else {
      labels.vin = this.translateService.instant('VEHICLE_SPACE.VIN');
    }

    // Vehicle title
    if (this.company.ws_vehicle_title && this.hasAdvancedVehicleFields()) {
      labels.vehicle_name = this.company.ws_vehicle_title;
    }
    else {
      labels.vehicle_name = 'Vehicles';
    }

    return labels;
  }

  getLetterhead () : string {
    return `${ this.company.letterhead }?v=${ this.letterheadVersion }`;
  }

  getLogo () : string {
    return `${ this.company.logo }?v=${ this.logoVersion }`;
  }

  getPaymentAccounts () : Promise<any> {
    if (this.hasIntegration('xero')) {
      return this.xeroService.getSettings()
      .then(response => response.chart_of_accounts.payment_accounts)
      .catch(() => []);
    }

    return Promise.resolve([]);
  }

  getTaxName () : string {
    return this.country('australia')
      ? 'GST'
      : '';
  }

  getUKPostcodeRequests () : number {
    return this.company.uk_postcode_requests || 0;
  }

  getUtcOffset () : number {
    return this.company.utc_offset || 0;
  }

  getUtcOffsetFromCode ( code : string, utcOffsets : Array<UtcOffset> ) : string {
    const selectedOffset = utcOffsets.find(o => o.code === code);

    return selectedOffset
      ? selectedOffset.utc_offset
      : null;
  }

  hasActiveAccountingIntegration () : boolean {
    return this.hasIntegration('myob')
    || this.hasIntegration('quickbooks')
    || this.hasIntegration('xero');
  };

  hasAdvancedVehicleFields () : boolean {
    return this.company.advanced_vehicle_fields;
  }

  hasDealership () : boolean {
    return this.company.has_dealership;
  }

  hasDefaultLaborProduct () : boolean {
    return !!this.company.company_settings.default_labour_product_name;
  }

  hasIntegration ( integration : string ) : boolean {
    const session = this.sessionService.base();

    switch (integration) {
      case 'ams-rewards':
        return this.company.ams_rewards_settings.has_ams_rewards;
      case 'ashdown':
        return this.isIntegrationAvailable(integration)
          && this.company.has_ashdown_ingram;
      case 'assembly-payments':
        return this.isIntegrationAvailable(integration)
          && this.company.pos_terminal_settings.has_assembly_payments;
      case 'autoinspect':
        return this.company.has_autoinspect;
      case 'burson':
        return this.company.burson_ezyparts_settings.has_burson_ezyparts;
      case 'business-intelligence':
        return session.gold_or_higher &&
          session.user_settings.business_intelligence_user;
      case 'capricorn':
        return this.company.capricorn_settings.has_capricorn;
      case 'carfax':
        return this.company.has_carfax
          && this.country([ 'canada', 'usa' ])
          && !this.isDealer();
      case 'celero':
        return this.isIntegrationAvailable(integration)
          && this.company.tnp_settings.has_tnp;
      case 'customer-messages':
        return this.isIntegrationAvailable('customer-messages')
          && this.company.has_active_sms_global_account;
      case 'customer-portal':
        return this.isIntegrationAvailable(integration) &&
          this.company.company_settings.has_customer_portal;
      case 'dvla-lookup':
        return this.company.has_dvla_lookup &&
          this.country([ 'ireland', 'northernIreland', 'unitedKingdom' ]);
      case 'gsf':
        return this.isIntegrationAvailable('gsf')
          && this.company.gsf_car_parts_settings
          && this.company.gsf_car_parts_settings.has_gsf_car_parts;
      case 'haynes-pro':
        return this.isIntegrationAvailable(integration)
          && this.company.haynes_pro_settings.has_haynes_pro;
      case 'haynes-pro-lookup':
        return this.isIntegrationAvailable('haynes-pro')
          && (this.company.has_haynes_pro_lookup || this.company.has_haynes_pro_trial);
      case 'hsy':
        return this.isIntegrationAvailable(integration)
          && this.company.hsy_settings.has_hsy;
      case 'mitchell1':
        return this.isIntegrationAvailable(integration)
          && this.company.mitchell1_settings.has_mitchell1;
      case 'myob':
        return this.company.has_myob;
      case 'oscar':
        return this.isIntegrationAvailable(integration) &&
          this.company.oscar_settings.has_oscar;
      case 'parts-authority':
        return this.isIntegrationAvailable(integration)
          && this.company.parts_authority_settings.has_parts_authority;
      case 'partstech':
        return this.isIntegrationAvailable(integration) &&
          this.company.partstech_settings.has_partstech;
      case 'partstech-labor':
        return this.hasIntegration('partstech')
          && this.company.partstech_settings.has_partstech_labour_times;
      case 'prolink':
        return this.isIntegrationAvailable(integration)
          && this.company.has_prolink;
      case 'quickbooks':
        return this.company.has_quickbooks;
      case 'registration-lookup':
        return this.country('australia')
          && this.company.registration_lookup_settings.has_registration_lookup
          && !this.isDealer();
      case 'repco':
        return this.isIntegrationAvailable(integration)
          && this.company.has_repco;
      case 'stripe':
        return this.isIntegrationAvailable('stripe')
          && this.company.stripe_settings.has_stripe;
      case 'tireconnect':
        return this.isIntegrationAvailable(integration) &&
          this.company.tireconnect_settings.has_tireconnect;
      case 'tiremetrix':
        return this.company.tiremetrix_settings.has_tiremetrix_tire_registration ||
        this.company.tiremetrix_settings.has_tiremetrix_tpms;
      case 'tillpayments-online':
        return this.isIntegrationAvailable('tillpayments')
          && this.company.tillpayments_settings.has_tillpayments_payment_gateway;
      case 'tillpayments-pos':
        return this.isIntegrationAvailable('tillpayments')
          && this.company.tillpayments_settings.has_tillpayments_eft;
      case 'tillpayments':
        return this.hasIntegration('tillpayments-online')
          || this.hasIntegration('tillpayments-pos');
      case 'uk-postcode':
        return this.company.has_uk_postcode &&
          this.country([ 'northernIreland', 'unitedKingdom' ]);
      case 'vehicle-registrations':
        return this.hasIntegration('registration-lookup')
          || (this.country('newZealand') && this.company.has_checka);
      case 'vehicle-visuals':
        return this.company.has_vehicle_visuals;
      case 'video-minutes':
        return this.company.has_video_minutes;
      case 'vvgarage':
        return this.company.vv_garage_settings.has_vv_garage;
      case 'xero':
        return this.company.has_xero;
      default:
        return false;
    }
  }

  hasMultiTax () : boolean {
    return !!this.company.use_multi_tax;
  }

  hasOnlinePayments () : boolean {
    return this.hasIntegration('stripe')
      || this.hasIntegration('tillpayments-online');
  }

  hasPOS () : boolean {
    return this.hasIntegration('assembly-payments')
      || this.hasIntegration('celero')
      || this.hasIntegration('tillpayments-pos');
  }

  hasPurchaseAccounts () : boolean {
    return this.company.company_accounting_settings.use_product_for_purchase_account
    && ( this.hasIntegration('myob') || this.hasIntegration('xero') );
  }

  hasReservedStock  () : boolean {
    return this.company.reserved_stock;
  }

  hasSecurityRights ( right : string ) : boolean {
    return this.company.company_security_settings
      && this.company.company_security_settings[right];
  }

  hasSmsNumber () : boolean {
    return !!this.company.sms_global_account_settings?.sms_global_number;
  }

  hasWorkshop () : boolean {
    return this.company.has_workshop;
  }

  isAccountingSetup () : boolean {
    return this.company.accounting_is_setup;
  }

  isActive ( company = this.company ) : boolean {
    return company.subscription_status === 'active';
  }

  isAzureLoginRequired ( company = this.company ) : boolean {
    return company.sso_settings.azure_required_login;
  }

  isBosch () {
    const brand = this.company.branding_group;

    return brand === 'bosch'
        || brand === 'autocrew'
        || brand === 'bosch_gen';
  }

  isBranchDealer () : boolean {
    return this.company.dealer_multibranch
        || this.company.dealer_multibranch_version;
  }

  isBranchOwner () : boolean {
    return this.isMultiBranch()
      && this.company.is_branch_owner;
  }

  isBranchStore () : boolean {
    return this.isDealerOrBranch()
      && !this.company.is_branch_owner;
  }

  isBrand  ( brand : string , company = this.company ) : boolean {
    return company.branding_group === brand;
  }

  isBranded () : boolean {
    return this.isBosch()
      || this.isBrand('nrma')
      || this.isBrand('tread');
  }

  isBrandPuppet  ( brand : string ) : boolean {
    const session = this.sessionService.base();

    return session.puppet_session
      && this.isBrand(brand)
      && !this.sessionService.dealer_superuser();
  }

  isDealer ( company : any = this.company ) : boolean {
    return !!company.dealer;
  }

  isDealerMultibranchStore () : Promise<boolean> {
    return this.apiService.get('/company/check_dealer_multibranch_status');
  }

  isDealerOrBranch () : boolean {
    return !!this.company.branch_id;
  }

  isFlatRateEnabled  () : boolean {
    return this.company.company_settings.enable_flat_rate;
  }

  isFranchiseOwner ( company = this.company ) : boolean{
    return !!company.is_franchise_owner;
  }

  isIntegrationAvailable ( integration : string ) : boolean {
    const session = this.sessionService.base();

    switch (integration) {
      case 'ams-rewards':
        return this.country('australia')
          && session.gold_or_higher;
      case 'ashdown':
        return this.country([ 'australia', 'newZealand' ]);
      /**
       * Now that we have rolled out WPAY, we want all new customers
       * to use that integration instead of Westpac (assembly payments).
       * So only show the menu item if a customer already has it activated.
       *
       * REFERENCE: WS-4675
       */
      case 'assembly-payments':
        return this.country('australia')
          && this.company?.pos_terminal_settings?.has_assembly_payments;
      case 'burson':
        return this.country(['australia', 'newZealand']);
      case 'capricorn':
        return this.country(['australia', 'newZealand'])
          && this.isSubscriptionPlan('silver', false);
      case 'carfax':
        return this.country([ 'canada', 'puertoRico', 'usa']);
      case 'celero':
        return this.country(['usa', 'canada']);
      case 'customer-messages':
        return this.country([
          'australia',
          'newZealand',
          'northernIreland',
          'unitedKingdom'
        ]);
      case 'customer-portal':
        return session.gold_or_higher;
      case 'dvla-lookup':
        return this.country(['unitedKingdom', 'northernIreland', 'ireland']);
      case 'gsf':
        return this.country('unitedKingdom');
      case 'haynes-pro':
        return this.country([
          'australia',
          'ireland',
          'newZealand',
          'northernIreland',
          'unitedKingdom',
        ]);
      case 'hsy':
        return this.country([ 'australia', 'newZealand' ])
          && this.isSubscriptionPlan('silver', false);
      case 'mitchell1':
        return this.country(['usa', 'canada']);
      case 'motor':
        return this.country([ 'canada', 'usa' ]);
      case 'myob':
        return this.country( ['australia', 'newZealand' ]);
      case 'oscar':
        return this.country([ 'australia', 'newZealand' ])
          && this.isSubscriptionPlan('silver', false);
      case 'parts-authority':
        return this.country(['usa', 'canada']);
      case 'partstech':
        return this.country([ 'canada', 'usa' ]);
      case 'prolink':
        return this.country([ 'australia', 'newZealand' ]);
      case 'repco':
        return this.country([ 'australia', 'newZealand' ]);
      case 'stripe':
        return this.country([
          'australia',
          'austria',
          'belgium',
          'brazil',
          'bulgaria',
          'canada',
          'cyprus',
          'czech',
          'denmark',
          'estonia',
          'france',
          'germany',
          'greece',
          'hongKong',
          'hungary',
          'india',
          'ireland',
          'italy',
          'japan',
          'latvia',
          'lithuania',
          'luxembourg',
          'malaysia',
          'malta',
          'mexico',
          'netherlands',
          'newZealand',
          'northernIreland',
          'norway',
          'poland',
          'portugal',
          'romania',
          'singapore',
          'slovakia',
          'slovenia',
          'spain',
          'sweden',
          'switzerland',
          'uae',
          'unitedKingdom',
          'usa'
        ]);
      case 'tillpayments':
        return this.country([
          'australia',
          'ireland',
          'newZealand',
          'unitedKingdom',
          'usa'
        ]);
      case 'tireconnect':
        return this.country([ 'canada', 'usa' ]);
      case 'tiremetrix':
        return this.country('usa');
      case 'uk-postcode':
        return this.country([ 'unitedKingdom', 'northerIreland' ]);
      case 'vehicle-registrations':
        return this.country(['australia', 'newZealand']);
      case 'westpac':
        const flags = session.flags;

        return this.isIntegrationAvailable('assembly-payments')
          && flags.has_westpac_terminal
          && !flags.has_till_payments_terminal;
      default:
        return false;
    }
  }

  isLinkedToBranch () : boolean {
    return this.isMultiBranch()
      && this.company.linked_to_branch;
  }

  isMultiBranch () : boolean {
    return this.company.is_multibranch;
  }

  isNRMAUser ( securityType : string ) : boolean {
    return this.hasSecurityRights(securityType)
      && !this.isBrandPuppet('nrma')
      && this.isBrand('nrma');
  }

  isSharedAccounting () : boolean {
    return this.isMultiBranch()
      && this.company.shares_accounting;
  }

  isState ( state : string ) : boolean {
    return this.company.state === state;
  }

  isSubscriptionPlan ( type : CompanySubscriptionPlansModel = 'bronze', matchExactly = true ) : boolean {
    const session = this.sessionService.base();

    switch (type) {
      case 'bronze':
        return !session.silver_or_higher;
      case 'gold':
        return matchExactly
          ? ( session.gold_or_higher && !session.platinum_or_higher )
          : session.gold_or_higher;
      case 'platinum':
        return session.platinum_or_higher;
      case 'silver':
        return matchExactly
          ? ( session.silver_or_higher && !session.gold_or_higher && !session.platinum_or_higher )
          : session.silver_or_higher;
    }
  }

  isTrialing ( company = this.company ) : boolean {
    return company.subscription_status === 'trialing';
  }

  isTwoFactorAuthEnabled ( companySettings = this.company.company_settings ) : boolean {
    return companySettings.enable_two_factor_auth;
  }

  isTwoFactorAuthEnforced ( companySettings = this.company.company_settings ) : boolean {
    return companySettings.enforce_two_factor_auth;
  }

  language ( language : string ) : boolean {
    const languages = {
      uk : 'en-uk',
      usa: 'en-us'
    };

    return this.company.language === languages[language];
  }

  priceIncludesTax ( company = this.company ) : boolean {
    return company.price_includes_tax;
  }

  pushDownVendorsAndProducts ( company = this.company ) : Promise<any> {
    return this.apiService.post('/motorserve/attach_franchise_vendors_and_products', {
      company_id: company.id
    });
  }

  saveAccountingSettings ( settings : any ) : Promise<any> {
    return this.apiService.patch('/company_accounting_settings', settings, 'company_accounting_setting');
  }

  setCompany ( company = this.sessionService.company() ) : void {
    this.company = company;
  }

  setImageVersion ( type : string ) : void {
    if ( type === 'logo' ) {
      this.logoVersion = Date.now();
    }
    else if ( type === 'letterhead' ) {
      this.letterheadVersion = Date.now();
    }
  }

  setUp () : Promise<void> {
    var resolves : any = {};

    return this.apiService.get('/company')
    .then(( company : any ) => {
      resolves.company = company;

      return this.apiService.get('/company_settings');
    })
    .then(( settings : CompanySettingsModel ) => {
      resolves.companySettings = settings;

      return this.apiService.get('/system/country_types', null, null, { bypass: true });
    })
    .then(( countries : Array<CodeTypeModel> ) => {
      resolves.countries = countries;

      return this.apiService.get('/system/states_for_country', resolves.company.country_code);
    })
    .then(( states : Array<CodeTypeModel> ) => {
      resolves.states = states;

      return this.apiService.get('/system/language_types/', null, 'language_types');
    })
    .then(( languages : Array<CodeTypeModel> ) => {
      resolves.languages = languages;

      return this.apiService.get('/system/phone_validations');
    })
    .then(( validations : CompanyCountryValidationsModel ) => {
      resolves.validations = validations[ resolves.company.country_code ];

      return this.getCompanyTypes();
    })
    .then(( companyTypes : Array<CodeTypeModel> ) => {
      const modal = this.modalService.open(CompanySetUpComponent, {
        windowClass: 'wizard'
      });

      resolves.companyTypes = companyTypes;

      for ( const key in resolves ) {
        modal.componentInstance[key] = resolves[key];
      }

      return modal.result;
    });
  }

  shareAccounting ( integration : string ) : Promise<void> {
    const modal = this.modalService.open(ShareAccoutingComponent);

    modal.componentInstance.integration = integration;

    return modal.result;
  }

  showFleetCode () : boolean {
    return this.company.show_fleet_code;
  }

  sortUtcOffset ( option : UtcOffset ) : number {
    const utcOffset = parseFloat(option.utc_offset);

    return utcOffset === -2
      ? -1
      : utcOffset;
  }

  updateSubscriptionPayment ( subscription : any ) : Promise<any> {
    return this.apiService.get('/system/country_types', null, null, { bypass: true })
    .then(( countries : Array<CodeTypeModel> ) => {
      const modal = this.modalService.open(UpdateSubscriptionPaymentComponent, {
        windowClass: 'md-modal'
      });

      modal.componentInstance.countries   = countries;

      /**
       * Sometimes, the call GET /company/get_chargify_subscription
       * doesn't return a credit card object. I have not found a type
       * of subscription that does this but to account for the
       * possibility, we will default it to an empty object to prevent
       * errors in the modal.
       *
       * REFERENCE: WS-5401
       */
      modal.componentInstance.paymentInfo = subscription.credit_card || {};

      return modal.result;
    });
  }

  usesMemberNumbers () : boolean {
    return this.company.company_settings.use_member_numbers;
  }

  usesMultiTax () : void {
    return this.company.use_multi_tax;
  }

  useTodayForPostDate () : boolean {
    return this.company.company_settings.use_today_as_invoice_post_date;
  }
}
